import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
} from "react-hook-form";

import Grid from "@mui/material/Grid";

import { Form, SubmitButton } from "@covertree/ui";
import { StepPath } from "@routing/paths";

interface Props<T extends FieldValues> {
  children: React.ReactNode;
  onSubmit: SubmitHandler<T>;
  form: UseFormReturn<T, object>;
  navigateToStep?: StepPath;
  buttonLabel?: string;
  dataTestId?: string;
}

export function ClaimsForm<T extends FieldValues>({
  children,
  form,
  onSubmit,
  navigateToStep,
  buttonLabel = "Next",
  dataTestId,
}: Props<T>) {
  const navigate = useNavigate();

  const { handleSubmit, formState } = form;

  const submit = useCallback(
    (formValue: T) => {
      onSubmit(formValue);

      if (navigateToStep) {
        navigate(`/${navigateToStep}`);
      }
    },
    [onSubmit, navigateToStep, navigate]
  );

  return (
    <Form onSubmit={handleSubmit(submit)} data-testid={dataTestId}>
      <FormProvider {...form}>
        <Grid container spacing={3} flexDirection="column" py={3}>
          {children}
          <Grid item xs={5} md={3}>
            <SubmitButton disabled={!formState.isValid}>
              {buttonLabel}
            </SubmitButton>
          </Grid>
        </Grid>
      </FormProvider>
    </Form>
  );
}
